/**
 * useCreateCreditNote — atomic WatermelonDB writer for desktop returns.
 *
 * Direct port of BigxBookApp/src/billing/useCreateCreditNote.ts. Steps run
 * inside ONE database.write():
 *   1. Generate CN-prefixed invoice number (device-local sequence)
 *   2. Recompute the returned lines the way the sale computed them
 *      (interstate aware, TAX_INCLUSIVE aware, discount proportioned)
 *   3. Create CREDIT_NOTE Invoice (referenceInvoiceId = original)
 *   4. Create LineItem rows for each returned product
 *   5. Create a single Payment row recording the refund tender
 *   6. StockMovement type=IN for each returned product, bumping currentStock
 *
 * All records are written `isSynced=false` so the existing CRDT engine
 * picks them up automatically.
 */

import { useCallback, useState } from 'react';
import { Model, Q } from '@nozbe/watermelondb';
import { getDbError, txn } from '@/db';
import { Invoice, LineItem, Payment } from '@/db/models/Billing';
import { Batch, Product, StockMovement } from '@/db/models/Inventory';
import { planBatchRestore } from '@/inventory/batchAllocation';
import { outstandingBatchDraw } from '@/inventory/saleAllocations';
import Store from '@/db/models/Store';
import { useAppStore } from '@/store/useAppStore';
import { BILLING_CREDIT_NOTE, hasPermission } from '@/rbac';
import { computeReturnLine, generateInvoiceNumber, toPaisa, toRupees } from './billingEngine';
import { applyRounding } from '@/utils/gstUtils';
import { getCartConfig } from '@/store/useCartStore';
import { loadRemainingReturnable } from './returnLedgerDataSource';
import { validateReturn } from './returnLedger';
import { findOpenSessionId } from './useBillingSession';
import { scheduleSyncPush } from '@/sync/scheduleSync';
import { logActivity } from '@/utils/auditLog';

export interface ReturnItem {
    lineItemId: string;
    productId: string;
    quantityToReturn: number;
    unitPrice: number;
    hsnCode: string | null;
    unit: string;
    gstRate: number;
    cessRate: number;
}

export interface CreateCreditNoteResult {
    success: boolean;
    invoiceId?: string;
    invoiceNumber?: string;
    error?: string;
}

export type RefundMethod = 'CASH' | 'UPI' | 'CARD' | 'CREDIT' | 'BANK_TRANSFER';

export function useCreateCreditNote() {
    const [isCreating, setIsCreating] = useState(false);

    const createCreditNote = useCallback(async (
        originalInvoiceId: string,
        returnItems: ReturnItem[],
        refundAmount: number,
        refundMethod: RefundMethod,
        notes: string,
    ): Promise<CreateCreditNoteResult> => {
        if (returnItems.length === 0) {
            return { success: false, error: 'Select at least one item to return' };
        }
        const { activeStoreId, currentUser, deviceSlot, deviceId, nextDeviceInvoiceNo } = useAppStore.getState();
        if (!currentUser || !activeStoreId) {
            return { success: false, error: 'Not authenticated or no active store.' };
        }

        // Authorisation at the write boundary — see the same check in
        // useCreateInvoice. A credit note moves money back to a customer and
        // restores stock, so it is gated on BILLING_CREDIT_NOTE rather than on
        // whether a screen happened to be reachable.
        if (!hasPermission(currentUser.role || '', BILLING_CREDIT_NOTE)) {
            return { success: false, error: 'Your role does not allow processing returns.' };
        }
        // Refuse to write into a database that cannot save. On quota exhaustion
        // WatermelonDB silently falls back to an in-memory adapter, so without
        // this check the till keeps taking sales and printing receipts while
        // nothing is persisted — every one of them lost on the next reload.
        // The same applies after another tab has overwritten this one's data.
        const dbError = getDbError();
        if (dbError) {
            return { success: false, error: `Cannot save to this device: ${dbError}` };
        }

        // #2 fix: credit-note numbers share the device's server-provisioned invoice
        // series (deviceSlot). A hashed 0-99 guess is not unique across devices and
        // collides on sync. Refuse until this device is activated online.
        const resolvedSlot = deviceSlot;
        if (resolvedSlot == null) {
            return {
                success: false,
                error: 'This device is not activated for billing yet. Connect to the internet once to activate it, then try again.',
            };
        }

        // A refund "to khata" credits the customer's account instead of paying
        // them; no money leaves the drawer.
        const isAccountCredit = refundMethod === 'CREDIT';

        setIsCreating(true);
        try {
            const result = await txn(async (db) => {
                const seqNo = nextDeviceInvoiceNo();
                const store = await db.get<Store>('stores').find(activeStoreId);
                const originalInvoice = await db.get<Invoice>('invoices').find(originalInvoiceId);
                // Reuse the store-configured prefix where present, otherwise CN.
                const cnPrefix = `CN${store.invoicePrefix ? `-${store.invoicePrefix}` : ''}`;
                const cnNo = generateInvoiceNumber(seqNo, cnPrefix, resolvedSlot);

                // ── Per-line tax recomputation ──
                //
                // A return credits what the customer actually PAID for the units
                // coming back, so it runs through the same computeLineItemTax()
                // the sale did — see computeReturnLine(). The old arithmetic here
                // credited qty × unitPrice and added GST on top, which ignored the
                // store's TAX_INCLUSIVE convention (refunding tax never collected)
                // and ignored the line's discount (refunding a discount never
                // given). Both over-refunded, and the discount one on every store.
                //
                // The original lines are the source of truth for quantity and
                // discount; the caller only says what is coming back.
                const originalLines = await db.get<LineItem>('line_items').query(
                    Q.where('invoice_id', originalInvoiceId),
                    Q.where('is_deleted', false),
                ).fetch();
                const originalById = new Map(originalLines.map((li) => [li.id, li]));

                // ── Ledger check ──
                // The same goods could be returned over and over: the screen capped
                // each line at the quantity SOLD and never subtracted earlier returns,
                // and nothing here checked either, so reopening a sale produced another
                // refund and another stock increase. `referenceInvoiceId` was written on
                // every credit note and read by nobody.
                //
                // Checked here as well as on the screen because a screen opened before
                // another device's return synced is showing a stale cap, and the refund
                // is the last place to catch it.
                const currentSessionId = await findOpenSessionId(activeStoreId, deviceId || '');
                const remaining = await loadRemainingReturnable(db, originalInvoiceId);
                const check = validateReturn(
                    returnItems.map((i) => ({ lineItemId: i.lineItemId, quantityToReturn: i.quantityToReturn })),
                    remaining,
                    (id) => originalById.get(id)?.description ?? 'that item',
                );
                if (!check.ok) throw new Error(check.error);

                let subtotal = 0, cgst = 0, sgst = 0, igst = 0, cess = 0, totalTax = 0, totalAmount = 0;
                const computed = returnItems.map((item) => {
                    const orig = originalById.get(item.lineItemId);
                    // Without the original line there is nothing to reverse. The
                    // amounts it recorded ARE the refund; recomputing them from
                    // today's price list and tax mode is what this fix removes,
                    // so guessing here would reintroduce it by the back door.
                    if (!orig) {
                        throw new Error(
                            'Cannot process this return: the original invoice line is missing. ' +
                            'Sync this device and try again.',
                        );
                    }
                    const line = computeReturnLine({
                        quantityToReturn: item.quantityToReturn,
                        originalQuantity: orig.quantity,
                        gstRate: item.gstRate,
                        cessRate: item.cessRate,
                        // Reverse what was charged, exactly as recorded.
                        original: {
                            taxableAmountPaisa: toPaisa(orig.taxableAmount),
                            cgstPaisa: toPaisa(orig.cgstAmount),
                            sgstPaisa: toPaisa(orig.sgstAmount),
                            igstPaisa: toPaisa(orig.igstAmount),
                            cessPaisa: toPaisa(orig.cessAmount),
                            totalPaisa: toPaisa(orig.totalAmount),
                            discountAmountPaisa: toPaisa(orig.discountAmount),
                        },
                    });
                    const lineTaxable = toRupees(line.taxableAmountPaisa);
                    const lCgst = toRupees(line.cgstPaisa);
                    const lSgst = toRupees(line.sgstPaisa);
                    const lIgst = toRupees(line.igstPaisa);
                    const lCess = toRupees(line.cessPaisa);
                    const lTotal = toRupees(line.totalPaisa);
                    const lDiscount = toRupees(line.discountAmountPaisa);
                    subtotal += lineTaxable;
                    cgst += lCgst; sgst += lSgst; igst += lIgst; cess += lCess;
                    totalTax += lCgst + lSgst + lIgst + lCess;
                    totalAmount += lTotal;
                    return { item, lineTaxable, lCgst, lSgst, lIgst, lCess, lTotal, lDiscount };
                });

                // Round the way a SALE rounds, not the way Math.round does.
                //
                // This was `Math.round(totalAmount)` — permanently "nearest
                // rupee", whatever the store is actually configured to. A shop
                // on the default NONE sold at ₹309.99 and refunded ₹310.00 for
                // the identical goods: the customer got a paisa more than they
                // paid, the books did not balance, and GSTR-1 could not
                // reconcile the credit note against the invoice it names.
                //
                // A credit note reverses a specific invoice, so it has to be
                // computed on the same terms that invoice was. Sharing
                // getCartConfig()/applyRounding with the sale path makes that
                // true by construction rather than by two functions agreeing to
                // round the same way.
                //
                // Caveat worth knowing: this reads the store's CURRENT mode, so
                // changing the rounding setting between a sale and its return
                // would still round the two differently. Pinning a credit note
                // to its parent invoice's round-off would close that too, and
                // needs the full-versus-partial return distinction to do it
                // properly.
                const preRoundTotalPaisa = toPaisa(totalAmount);
                const finalTotalPaisa = applyRounding(preRoundTotalPaisa, getCartConfig().roundingMode);
                const finalTotal = toRupees(finalTotalPaisa);
                // Subtract in paisa, convert once — the same discipline the sale
                // path uses. In rupees this is float subtraction, and
                // 310.00 - 309.99 is 0.010000000000019 rather than 0.01.
                const roundOff = toRupees(finalTotalPaisa - preRoundTotalPaisa);

                // ── Phase 2 · Prepare every write. Nothing is committed here. ──
                //
                // A return used to write the credit note, its lines, the refund
                // payment, the product updates and the stock movements as
                // SEPARATE committed steps inside one writer. A writer is a lock,
                // not a transaction, so a failure part-way left the earlier steps
                // standing: the refund recorded, the goods never put back. Worse,
                // the duplicate-return guard then counted those units as returned,
                // so the retry was refused and the stock stayed lost.
                //
                // Same shape as useCreateInvoice: prepare everything, apply once.
                const ops: Model[] = [];

                // 1. Credit-note Invoice
                const cnInvoice = db.get<Invoice>('invoices').prepareCreate((record) => {
                    record.storeId = activeStoreId;
                    record.customerId = originalInvoice.customerId;
                    record.createdById = currentUser.id;
                    record.invoiceNumber = cnNo;
                    record.type = 'CREDIT_NOTE';
                    record.referenceInvoiceId = originalInvoice.id;
                    record.status = 'CONFIRMED';
                    record.paymentStatus = 'PAID';
                    record.subtotal = subtotal;
                    record.discountType = null;
                    record.discountValue = 0;
                    record.discountAmount = 0;
                    record.promotionId = null;
                    record.taxableAmount = subtotal;
                    record.cgstAmount = cgst;
                    record.sgstAmount = sgst;
                    record.igstAmount = igst;
                    record.cessAmount = cess;
                    record.totalTax = totalTax;
                    record.roundOff = roundOff;
                    record.totalAmount = finalTotal;
                    // Money actually handed back. A khata refund hands back
                    // nothing — it is settled by reducing what the customer
                    // owes — so recording it as paid would misreport the cash
                    // this return cost the shop.
                    record.paidAmount = isAccountCredit ? 0 : refundAmount;
                    record.dueAmount = 0;
                    record.currency = originalInvoice.currency;
                    record.notes = notes || `Return against ${originalInvoice.invoiceNumber}`;
                    record.termsConditions = null;
                    record.isInterState = originalInvoice.isInterState;
                    record.isEInvoiced = false;
                    record.irn = null;
                    record.eInvoiceData = null;
                    record.pdfUrl = null;
                    record.whatsappSent = false;
                    record.deviceId = deviceId || null;
                    // The drawer the refund is paid FROM, not the one that made the
                    // original sale — which may be weeks closed. Day close subtracts
                    // refunds from the till they actually left.
                    record.billingSessionId = currentSessionId;
                    record.isEmergency = false;
                    record.version = 1;
                    record.isSynced = false;
                    record.isDeleted = false;
                });
                ops.push(cnInvoice);

                // 2. LineItems
                //
                // A returned unit must carry the cost it was SOLD at, not today's
                // purchase price. If the shop restocked between sale and return,
                // reading the product's current cost would make the credit note
                // reverse a different COGS than the sale booked, so a fully
                // returned item would not net its margin to zero.
                const costByLineItemId = new Map(originalLines.map((li) => [li.id, li.costPrice]));

                const lineItemCollection = db.get<LineItem>('line_items');
                for (const c of computed) {
                    ops.push(lineItemCollection.prepareCreate((record) => {
                        record.invoiceId = cnInvoice.id;
                        record.productId = c.item.productId;
                        record.variantId = null;
                        record.batchId = null;
                        // Carry the sold line's typed description. A typed item's
                        // product is the shared "Manual entry" placeholder, so
                        // without this the credit note prints "Manual entry", and
                        // the return ledger cannot tell typed lines apart.
                        record.description = originalById.get(c.item.lineItemId)?.description ?? null;
                        record.hsnCode = c.item.hsnCode;
                        record.quantity = c.item.quantityToReturn;
                        record.unit = c.item.unit;
                        record.unitPrice = c.item.unitPrice;
                        record.discountType = c.lDiscount > 0 ? 'FLAT' : null;
                        record.discountValue = c.lDiscount;
                        record.discountAmount = c.lDiscount;
                        record.taxableAmount = c.lineTaxable;
                        record.gstRate = c.item.gstRate;
                        record.cessRate = c.item.cessRate;
                        record.cgstAmount = c.lCgst;
                        record.sgstAmount = c.lSgst;
                        record.igstAmount = c.lIgst;
                        record.cessAmount = c.lCess;
                        record.totalAmount = c.lTotal;
                        record.costPrice = costByLineItemId.get(c.item.lineItemId) ?? null;
                        record.version = 1;
                        record.isSynced = false;
                        record.isDeleted = false;
                    }));
                }

                // 3. Refund Payment row — only when money actually moves.
                //
                // A Payment row against a CREDIT_NOTE means "the shop paid this
                // back". The server folds it as +amount on the customer's
                // balance, cancelling the −amount the credit note itself
                // applies, which is right for cash/UPI/card: the customer got
                // their money, so their debt is unchanged.
                //
                // "Credit (khata)" is the opposite — nothing is paid out, the
                // return is settled by reducing what they owe. Writing a
                // payment row for it cancelled exactly the reduction the
                // customer was owed, so a ₹100 khata return moved their balance
                // by ₹0. This is the same rule useCreateInvoice already applies
                // to a CREDIT tender on the sale side; only the return side had
                // been missed.
                if (refundAmount > 0 && !isAccountCredit) {
                    ops.push(db.get<Payment>('payments').prepareCreate((record) => {
                        record.invoiceId = cnInvoice.id;
                        record.method = refundMethod;
                        record.amount = refundAmount;
                        record.billingSessionId = currentSessionId;
                        record.transactionRef = null;
                        record.status = 'COMPLETED';
                        (record as unknown as { receivedAt: Date }).receivedAt = new Date();
                        record.notes = 'Refund';
                        record.version = 1;
                        record.isSynced = false;
                        record.isDeleted = false;
                    }));
                }

                // 4. Stock back IN — into the batches the sale drew from.
                //
                // Restoring only product.currentStock would leave batch.quantity short
                // forever: batch stock is derived from batch-tagged movements, so an
                // untagged IN never refills the batch it came out of.
                const stockMovementCollection = db.get<StockMovement>('stock_movements');
                for (const item of returnItems) {
                    const product = await db.get<Product>('products').find(item.productId);
                    if (product.isService || !product.trackInventory) continue;
                    ops.push(product.prepareUpdate((record) => {
                        record.currentStock += item.quantityToReturn;
                        record.isSynced = false;
                        record.version = product.version + 1;
                    }));

                    const draw = await outstandingBatchDraw(originalInvoice.id, product.id);
                    const plan = planBatchRestore(draw, item.quantityToReturn);

                    for (const allocation of plan.allocations) {
                        const batchRecord = await db.get<Batch>('batches')
                            .find(allocation.batchId).catch(() => null);
                        if (batchRecord) {
                            ops.push(batchRecord.prepareUpdate((record) => {
                                record.quantity = batchRecord.quantity + allocation.allocatedQty;
                                record.isSynced = false;
                                record.version = batchRecord.version + 1;
                            }));
                        }
                        ops.push(stockMovementCollection.prepareCreate((record) => {
                            record.productId = product.id;
                            record.godownId = null;
                            record.batchId = allocation.batchId;
                            record.type = 'IN';
                            record.quantity = allocation.allocatedQty;
                            record.referenceId = cnInvoice.id;
                            record.referenceType = 'INVOICE';
                            record.notes = `Return / Credit Note: ${cnNo}`;
                            record.source = 'DESKTOP';
                            record.userId = currentUser.id;
                            record.version = 1;
                            record.isSynced = false;
                            record.isDeleted = false;
                        }));
                    }

                    // Units we cannot attribute to a batch (non-batch product, or a sale
                    // that predates batch tagging) go back product-level, untagged.
                    if (plan.untagged > 0) {
                        ops.push(stockMovementCollection.prepareCreate((record) => {
                            record.productId = product.id;
                            record.godownId = null;
                            record.batchId = null;
                            record.type = 'IN';
                            record.quantity = plan.untagged;
                            record.referenceId = cnInvoice.id;
                            record.referenceType = 'INVOICE';
                            record.notes = `Return / Credit Note: ${cnNo}`;
                            record.source = 'DESKTOP';
                            record.userId = currentUser.id;
                            record.version = 1;
                            record.isSynced = false;
                            record.isDeleted = false;
                        }));
                    }
                }

                // ── Phase 3 · One atomic apply. Either the refund and the stock
                // restoration both land, or neither does.
                await db.batch(...ops);

                return { invoiceId: cnInvoice.id, invoiceNumber: cnNo };
            });

            void logActivity({
                action: 'credit_note_created',
                entityType: 'invoice',
                entityId: result.invoiceId,
                storeId: activeStoreId,
                userId: currentUser.id,
                changes: {
                    summary: `${result.invoiceNumber} · ${returnItems.length} item${returnItems.length === 1 ? '' : 's'} returned · refund ${refundMethod}`,
                    amount: -refundAmount,
                    quantity: -returnItems.reduce((sum, it) => sum + it.quantityToReturn, 0),
                    ref: result.invoiceNumber,
                    after: { refund: refundAmount, method: refundMethod },
                },
            });

            // Realtime: flush this credit note to the server soon (fire-and-forget).
            scheduleSyncPush();

            return {
                success: true,
                invoiceId: result.invoiceId,
                invoiceNumber: result.invoiceNumber,
            };
        } catch (error: unknown) {
            console.error('[useCreateCreditNote] Failed:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error creating credit note',
            };
        } finally {
            setIsCreating(false);
        }
    }, []);

    return { createCreditNote, isCreating };
}
